// /dashboard/admin/roles/new — 建立自訂角色 wizard.
// Spec: docs/specs/custom-rbac.md §6.2.
//
// Two-step flow:
//   1. 基本資料 + 範本選擇（空白 / 複製自系統角色）
//   2. 權限選擇（PermissionPicker，預先勾上範本的權限）

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Save, ShieldCheck } from "lucide-react";
import PageHeader from "../../components/PageHeader.js";
import { useConfirmDialog } from "../../components/ConfirmDialog.js";
import { useDashboardSession } from "../../auth/useDashboardSession.js";
import TenantPickerEmptyState from "../../components/TenantPickerEmptyState.js";
import PermissionPicker from "../../components/PermissionPicker.js";
import {
  listRoles,
  getRole,
  createRole,
  replaceRolePermissions,
  friendlyRolesError,
  type RoleRow,
} from "../../lib/rolesApi.js";

type Step = 1 | 2;

export default function RoleCreate(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const nav = useNavigate();
  const dlg = useConfirmDialog();

  const [step, setStep] = useState<Step>(1);
  const [systemRoles, setSystemRoles] = useState<RoleRow[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  /** "" = 從空白開始；otherwise = system role id (e.g. "sys_manager"). */
  const [clonedFrom, setClonedFrom] = useState<string>("");
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [loadingTemplatePerms, setLoadingTemplatePerms] = useState(false);

  // Load system roles for the template picker.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await listRoles(tenantParam);
        if (!alive) return;
        setSystemRoles(r.items.filter((x) => x.is_system));
      } finally {
        if (alive) setLoadingTemplates(false);
      }
    })();
    return () => { alive = false; };
  }, [tenantParam]);

  // When jumping to step 2, hydrate permission set from chosen template.
  const goToStep2 = useCallback(async () => {
    if (!name.trim()) {
      await dlg.alert("請先輸入角色名稱");
      return;
    }
    if (clonedFrom) {
      setLoadingTemplatePerms(true);
      try {
        const detail = await getRole(clonedFrom, tenantParam);
        setPermissions(new Set(detail.permissions));
      } catch (err) {
        await dlg.alert(friendlyRolesError(err), { title: "無法載入範本權限" });
        return;
      } finally {
        setLoadingTemplatePerms(false);
      }
    } else {
      setPermissions(new Set());
    }
    setStep(2);
  }, [clonedFrom, dlg, name, tenantParam]);

  async function handleSave(): Promise<void> {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const ids = Array.from(permissions);
      const created = await createRole(
        { name: name.trim(), description: description.trim() || null, permission_ids: ids, cloned_from: clonedFrom || null },
        tenantParam,
      );
      // Some backends return `id` from create + apply permissions in one call;
      // we call replaceRolePermissions defensively in case the create endpoint
      // ignores `permission_ids` for now. If the server already applied them
      // PUT is idempotent (added=0, removed=0).
      if (ids.length > 0) {
        try {
          await replaceRolePermissions(created.id, ids, tenantParam);
        } catch {
          // best-effort — create already succeeded; permissions are subsequently editable on detail page.
        }
      }
      nav(`/dashboard/admin/roles/${encodeURIComponent(created.id)}`);
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: "建立失敗" });
    } finally {
      setSubmitting(false);
    }
  }

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="建立自訂角色" />
        <TenantPickerEmptyState />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="建立自訂角色"
        description={step === 1 ? "步驟 1 / 2：基本資料與範本" : "步驟 2 / 2：權限設定"}
      />

      {step === 1 ? (
        <Step1
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          clonedFrom={clonedFrom}
          setClonedFrom={setClonedFrom}
          systemRoles={systemRoles}
          loading={loadingTemplates}
          onCancel={() => nav("/dashboard/admin/roles")}
          onNext={() => void goToStep2()}
          loadingNext={loadingTemplatePerms}
        />
      ) : (
        <Step2
          name={name}
          permissions={permissions}
          setPermissions={setPermissions}
          onBack={() => setStep(1)}
          onSave={() => void handleSave()}
          submitting={submitting}
        />
      )}
    </>
  );
}

function Step1(props: {
  name: string;
  setName: (s: string) => void;
  description: string;
  setDescription: (s: string) => void;
  clonedFrom: string;
  setClonedFrom: (s: string) => void;
  systemRoles: RoleRow[];
  loading: boolean;
  onCancel: () => void;
  onNext: () => void;
  loadingNext: boolean;
}): React.ReactElement {
  const { name, setName, description, setDescription, clonedFrom, setClonedFrom, systemRoles, loading, onCancel, onNext, loadingNext } = props;
  return (
    <div className="max-w-2xl space-y-6">
      <div className="card p-5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">名稱 *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例：區域督導"
            maxLength={120}
            className="input"
          />
          <div className="mt-1 text-2xs text-text-muted">
            {name.length} / 120 字
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">描述</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="（選填）例：負責中部三個案場的督導工作"
            rows={3}
            className="textarea"
          />
        </div>
      </div>

      <div className="card p-5 space-y-3">
        <h3 className="text-sm font-semibold text-text-primary">範本（選一個起點）</h3>
        <p className="text-2xs text-text-muted -mt-2">
          下一步可以再增刪權限；範本只是預設選項。
        </p>
        <div className="space-y-2">
          <TemplateOption
            id=""
            label="從空白開始"
            description="後續手動勾選每個權限"
            checked={clonedFrom === ""}
            onChange={setClonedFrom}
          />
          {loading ? (
            <div className="text-2xs text-text-muted">載入系統角色…</div>
          ) : (
            systemRoles.map((r) => (
              <TemplateOption
                key={r.id}
                id={r.id}
                label={`複製自「${r.name}」`}
                description={r.description ?? `${r.permission_count} 個權限`}
                checked={clonedFrom === r.id}
                onChange={setClonedFrom}
              />
            ))
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" onClick={onCancel} className="btn-ghost text-sm">
          <ArrowLeft className="h-4 w-4" /> 取消
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={loadingNext || !name.trim()}
          className="btn-primary text-sm"
        >
          {loadingNext ? "載入範本…" : <>下一步 <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </div>
  );
}

function TemplateOption({ id, label, description, checked, onChange }: {
  id: string; label: string; description: string; checked: boolean; onChange: (id: string) => void;
}): React.ReactElement {
  return (
    <label className={[
      "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
      checked ? "border-accent bg-accent-subtle" : "border-border hover:bg-bg-hover",
    ].join(" ")}>
      <input
        type="radio"
        name="cloned_from"
        value={id}
        checked={checked}
        onChange={() => onChange(id)}
        className="mt-1 border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
      />
      <div className="min-w-0">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        <div className="text-2xs text-text-muted">{description}</div>
      </div>
    </label>
  );
}

function Step2(props: {
  name: string;
  permissions: Set<string>;
  setPermissions: (next: Set<string>) => void;
  onBack: () => void;
  onSave: () => void;
  submitting: boolean;
}): React.ReactElement {
  const { name, permissions, setPermissions, onBack, onSave, submitting } = props;
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-bg-raised border border-border px-4 py-2 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-accent" />
        <span className="text-sm text-text-secondary">即將建立角色「<strong className="text-text-primary">{name}</strong>」</span>
      </div>

      <PermissionPicker value={permissions} onChange={setPermissions} />

      <div className="flex items-center justify-between sticky bottom-0 bg-bg-base/95 backdrop-blur py-3 border-t border-border -mx-6 px-6 sm:mx-0 sm:px-0 sm:border-0 sm:bg-transparent sm:py-0">
        <button type="button" onClick={onBack} className="btn-ghost text-sm">
          <ArrowLeft className="h-4 w-4" /> 上一步
        </button>
        <button type="button" onClick={onSave} disabled={submitting} className="btn-primary text-sm">
          <Save className="h-4 w-4" /> {submitting ? "建立中…" : "建立角色"}
        </button>
      </div>
    </div>
  );
}
